import { groupBy } from 'lodash';
import { FieldSectionSubType } from 'api/field-sections/types';
import { HttpClient } from 'api/http-client';
import { Field } from 'api/tasks';
import { Color } from 'components/colors';
import { InitWorkerRequestType, InitWorkerApi } from 'initWorker';

export interface AccountFields {
  accountId: number | null;
  accountName: string | null;
  objectType: string | null;
  fields: Array<Field>;
}

export type FieldMetaDataOption = {
  color: Color;
  label: string;
  position?: number;
  group?: number;
  deletable?: boolean;
  editable?: boolean;
};

export type RatingFieldScale = 3 | 5 | 7 | 10;
export const RATING_SCALE = [3, 5, 7, 10];

export type RatingFieldMeta = {
  options: FieldMetaDataOption[];
  ratingScale?: number;
};

export interface SingleAndMultiSelectFieldMeta {
  options: FieldMetaDataOption[];
}
export interface NoteFieldMeta {
  tags: FieldMetaDataOption[];
}

export type KeyEventMeta = {
  captureKeyIntervalForFirstActivityOnly: boolean;
  allowAdminsAndOwnersToEdit: boolean;
};

export type IntervalMeta = {
  startKeyEventId: number;
  endKeyEventId: number;
  intervalBenchmark: number;
};

export type FieldMetaData =
  | RatingFieldMeta
  | KeyEventMeta
  | IntervalMeta
  | SingleAndMultiSelectFieldMeta
  | NoteFieldMeta;

interface Payload
  extends Pick<
    Field,
    | 'isEnabled'
    | 'fieldLabel'
    | 'description'
    | 'private'
    | 'fieldDataType'
    | 'fieldType'
    | 'fieldTypeDisplay'
    | 'objectType'
    | 'objectSubType'
    | 'fieldSectionId'
    | 'formulaFieldMeta'
  > {
  metaData?: FieldMetaData;
  value?: number;
}
export type FieldPayload = Partial<Payload>;

function getUrl(fieldId: number) {
  return `/fields/${fieldId}`;
}
const addIndexToFieldsGroupedByObjectType = (
  accountFields: AccountFields[],
): AccountFields[] => {
  return accountFields.map((accountField) => {
    const sortedFields = accountField.fields?.sort(
      ({ fieldId: fieldIdA }, { fieldId: fieldIdB }) => fieldIdA - fieldIdB,
    );

    const fieldsGrouped = groupBy(sortedFields, (field) =>
      field.objectSubType && field.objectSubType !== FieldSectionSubType.Default
        ? field.objectSubType
        : field.objectType,
    );

    accountField.fields = Object.values(fieldsGrouped).reduce(
      (allFields, fieldsInGroup) =>
        allFields.concat(
          fieldsInGroup.map((field, index) => ({
            ...field,
            fieldIndex: index,
          })),
        ),
      [],
    );

    return accountField;
  });
};

class FieldsHttpClient extends HttpClient {
  public getAllFields = async () => {
    let data: AccountFields[];

    try {
      data = (await InitWorkerApi.getData(
        InitWorkerRequestType.Fields,
      )) as AccountFields[];
    } catch {
      data = await this.get<AccountFields[]>('/all-fields');
    }

    return addIndexToFieldsGroupedByObjectType(data);
  };

  public async updateField(
    fieldId: number,
    data: FieldPayload,
  ): Promise<Field> {
    return this.put(getUrl(fieldId), data);
  }

  public async getField(fieldId: number): Promise<Field> {
    return this.get(getUrl(fieldId));
  }

  public async deleteField(fieldId: number): Promise<Field> {
    return this.delete(getUrl(fieldId));
  }

  public async createField(data: FieldPayload): Promise<Field> {
    return this.post('/fields', data);
  }

  public async getFieldProjectMapping(
    fieldId: number,
    value: number | undefined,
  ): Promise<Field> {
    return this.get(
      `${getUrl(fieldId)}/getIsStatusDeletePossible?value=${value}`,
    );
  }

  public getSkillFieldIdsForReadAccess = async (
    resource?: string,
  ): Promise<Field['fieldId'][]> => {
    return this.get(`/skill/fields${resource ? `?resource=${resource}` : ''}`);
  };
}

export const FieldsAPI = new FieldsHttpClient();
